import { ChangeDetectorRef, Component, ElementRef, ViewChild, AfterViewChecked } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ChatType, IChat, IChatItem, IResponse, SearchUser } from '@interfaces/index';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subscription, switchMap } from 'rxjs';
import { AppService } from '../services/app.service';
import { ChatService } from '../services/chat.service';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements AfterViewChecked {
  @ViewChild('questionInput') questionInputRef!: ElementRef<HTMLInputElement>;

  chats: IChat[] = [];
  chat: IChat | null = null;
  users: SearchUser[] = [];
  selectedUser: SearchUser | null = null;
  filteredUsers: string[] = [];
  showUsers = false;
  questionForm: FormGroup;
  loading = false;
  text: string = '';
  subscribe: Observable<IResponse<IChatItem>> | null = null;

  private subscriptions: Subscription[] = [];
  private shouldRefocusInput = false;
  private userWasJustSelected = false;

  constructor(
    protected _fb: FormBuilder,
    protected _app_service: AppService,
    protected _chat_service: ChatService,
    protected _cookie_service: CookieService,
    protected _user_service: UserService,
    private _cdr: ChangeDetectorRef
  ) {
    this.questionForm = this._fb.group({
      question: ['Give me questions for ', Validators.required]
    });
  }

  ngAfterViewChecked(): void {
    if (this.shouldRefocusInput && this.questionInputRef) {
      this.questionInputRef.nativeElement.focus();
      this.shouldRefocusInput = false;
    }
  }

  startNewChat() {
    this.chat = null;
  }

  trackByChatId(_index: number, chat: IChatItem): number {
    return chat.id;
  }

  trackByUserId(_index: number, user: SearchUser): number {
    return user.org_person_id;
  }

  sendQuestion() {
    if (this.questionForm.valid) {
      const coachee_person_id = this.selectedUser?.org_person_id ?? null;
      const coach_person_id = parseInt(this._cookie_service.get('person_id') || '0', 10);
      this.loading = true;

      const chatObservable = this.chat
        ? new Observable<IResponse<IChat>>((observer) => observer.next({ data: this.chat } as IResponse<IChat>))
        : this._chat_service.startNewChat();

      chatObservable.pipe(
        switchMap((response: IResponse<IChat>) => {
          if (!this.chat) {
            this.chats.push(response.data);
            this.chat = response.data;
          }

          return this._chat_service.sendQuestion(
            this.chat.id,
            this.questionForm.get('question')?.value,
            coach_person_id,
            coachee_person_id
          );
        }),
        switchMap((sendQuestionResponse: IResponse<IChatItem>) => {
          return this.handleQuestionResponse(sendQuestionResponse.data);
        })
      ).subscribe({
        error: (err) => {
          console.error('❌ Error in sendQuestion:', err);
          this._app_service.notify('Error processing request.', 'error');
        }
      });
    }
  }

  private handleQuestionResponse(questionItem: IChatItem) {
    this.questionForm.reset({
      question: 'Give me questions for '
    });    
    this.chat?.chats.push(questionItem);
    this.selectedUser = null;

    this.fetchOllieHelp(questionItem.id);
    this.fetchAnswer(questionItem.id);

    this.loading = false;
    return new Observable();
  }

  fetchOllieHelp(messageId: number) {
    this._chat_service.getOllieHelp(messageId).subscribe({
      next: (ollieHelpResponse) => {
        if (ollieHelpResponse.code === 200) {
          const ollieHelpChatItem: IChatItem = {
            id: new Date().getTime(),
            created_at: new Date().toISOString(),
            type_id: ChatType.OllieHelp,
            gerund: '',
            chat_id: this.chat!.id,
            message_id: 0,
            content: ollieHelpResponse.data,
            content_array: [ollieHelpResponse.data],
            org_person_id: 0,
            first_name: 'Ollie',
            last_name: '',
          };

          const insertIndex = this.chat?.chats.findIndex(
            (item) =>
              item.type_id === ChatType.Ollie &&
              item.message_id === messageId
          );

          if (insertIndex !== undefined && insertIndex > -1) {
            this.chat?.chats.splice(insertIndex, 0, ollieHelpChatItem);
          } else {
            this.chat?.chats.push(ollieHelpChatItem); // fallback if not found
          }
        }
      },
      error: () => {
        this._app_service.notify('Error fetching Ollie help.', 'error');
      }
    });
  }

  fetchAnswer(messageId: number) {
    if (!this.chat) {
      console.warn('⚠️ No chat available, skipping fetchAnswer.');
      return;
    }

    const chatItem: IChatItem = {
      id: new Date().getTime(),
      created_at: new Date().toISOString(),
      type_id: ChatType.Ollie,
      gerund: '',
      chat_id: this.chat.id,
      message_id: messageId,
      content: '',
      content_array: [],
      org_person_id: 0,
      first_name: 'Ollie',
      last_name: '',
      isStreaming: true,
    };

    this.chat.chats.push(chatItem);

    const seenMessages = new Set<string>();

    const subscription = this._chat_service.getAnswerStream(messageId).subscribe({
      next: (parsedChunk) => {
        if (!parsedChunk || !parsedChunk.text || parsedChunk.message_id !== messageId) return;

        if (!seenMessages.has(parsedChunk.text)) {
          seenMessages.add(parsedChunk.text);
          chatItem.content_array = [...chatItem.content_array, parsedChunk.text];
          chatItem.content = chatItem.content_array.join('\n');
          setTimeout(() => this._cdr.detectChanges(), 0);
        }
      },
      error: (err) => {
        console.error('❌ Streaming error:', err);
        chatItem.isStreaming = false;
        if (this.chat && this.chat.chats.length > 0) {
          this.chat.chats.pop();
        }
        setTimeout(() => this._cdr.detectChanges(), 0);
        subscription.unsubscribe();
      },
      complete: () => {
        chatItem.isStreaming = false;
        setTimeout(() => this._cdr.detectChanges(), 0);
        subscription.unsubscribe();
      }
    });

    this.subscriptions.push(subscription);
  }

  onRefresh(messageId: number) {
    if (this.chat) {
      this.loading = true;
      this.fetchAnswer(messageId);
      this.loading = false;
    }
  }

  updateFilteredUsers(event: any): void {
    const inputValue = event.target.value;
    const atIndex = inputValue.lastIndexOf("@");

    if (atIndex === -1) {
      this.showUsers = false;
      return;
    }

    const mentionCandidate = inputValue.slice(atIndex + 1).trim();
    const hasFinishedMention = mentionCandidate.length === 0 || /\s/.test(mentionCandidate.slice(-1));

    if (hasFinishedMention || this.selectedUser) {
      this.showUsers = false;
      return;
    }

    const person_id = parseInt(this._cookie_service.get('person_id'));
    this._user_service.search(person_id, mentionCandidate).subscribe(
      (response: IResponse<{ content: SearchUser[] }>) => {
        this.showUsers = true;
        this.users = response.data.content;
      }
    );
  }

  selectUser(user: SearchUser): void {
    const control = this.questionForm.get('question');
    if (control) {
      let inputValue = control.value;
      const atIndex = inputValue.lastIndexOf("@");

      if (atIndex !== -1) {
        const beforeAt = inputValue.substring(0, atIndex + 1);
        const afterAt = inputValue.substring(atIndex + 1).split(" ")[0];

        const newInputValue = inputValue.replace(
          `@${afterAt}`,
          `@${user.first_name} ${user.last_name} `
        );

        control.setValue(newInputValue);
        this.selectedUser = user;
        this.showUsers = false;
        this.shouldRefocusInput = true;
        this.userWasJustSelected = true;
      }
    }
  }
}
